import { ajax } from 'jquery'
import Evented from '../Evented'
import ISubscriptionConfig from '../interfaces/ISubscriptionConfig'
import JSONParseError from './JSONParseError'

const RETRY_DELAY = 2000
const TIMEOUT = 30000

function getParams (options: ISubscriptionConfig): { [key: string]: any } {
  return typeof options.params === 'function' ? options.params() : options.params || {}
}

function parseFrame (data: string) {
  try {
    return JSON.parse(data)
  } catch (e) {
    throw new JSONParseError(data)
  }
}

export default class RequestLoop extends Evented {
  public lastMessageSequence: number
  public didSuccessfullyConnect = false

  private uri: string
  private options: ISubscriptionConfig
  private lastRequest?: ReturnType<typeof ajax>
  private nextRequest?: ReturnType<typeof setTimeout>
  private stopRequestLoop = false
  private isConnected = false

  constructor (uri: string, lastMessageSequence: number, options: ISubscriptionConfig) {
    super()
    this.uri = uri
    this.lastMessageSequence = lastMessageSequence
    this.options = options
    this.request()
  }

  public stop () {
    this.stopRequestLoop = true
    if (this.lastRequest) {
      try {
        this.lastRequest.abort()
      } catch (error) { }
      delete this.lastRequest
    }
    if (this.nextRequest) {
      clearTimeout(this.nextRequest)
      delete this.nextRequest
    }
  }

  private request () {
    this.lastRequest = ajax({
      url: this.uri,
      method: 'POST',
      data: getParams(this.options),
      timeout: TIMEOUT,
      cache: false,
      crossDomain: true
    })
    this.lastRequest
      .then(this.onMessage.bind(this))
      .fail(this.onError.bind(this))
  }

  private onMessage (data: any, _: string, xhr: ReturnType<typeof ajax>) {
    if (this.stopRequestLoop) {
      return
    }
    if (!this.didSuccessfullyConnect) {
      this.didSuccessfullyConnect = true
      this.emit('connected', 'initial')
    }
    if (!this.isConnected) {
      this.isConnected = true
      this.emit('up')
    }
    if (xhr.status === 200) {
      const frame = parseFrame(data)
      if (frame.error === 'Subscription Failed') {
        this.emit('failed', 'subscription', frame)
      }
      if (frame.pong !== 'PONG') {
        const message = JSON.parse(frame.message)
        if (!message.error) {
          this.lastMessageSequence = frame.last_sequence
          this.nextRequest = setTimeout(this.request.bind(this), 10)
        }
        this.emit('message', message, frame)
      }
    } else if (xhr.status === 204) {
      this.emit('heartbeat')
      this.request()
    }
  }

  private onError (xhr: ReturnType<typeof ajax>) {
    if (this.stopRequestLoop) {
      return
    }
    if (this.didSuccessfullyConnect) {
      if (this.isConnected) {
        this.isConnected = false
        this.emit('down')
      }
      this.emit('reconnecting')
      const preflight = this.options.preflight
        ? this.options.preflight()
        : Promise.resolve()
      const timer = new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
      Promise.all([preflight, timer]).then(() => this.request())
    } else {
      this.emit('failed', 'connection', xhr)
    }
  }
}
